import { CSPaginationRequestArgs, PagedApiResponse } from "types/pagination";
import { ConstraintFilters, ConstraintTerms } from "consts";

import { ApiDateFilter } from "types/dates";
import { DateString } from "types/base";
import { SegmentPermissionType } from "consts/SegmentPermissionTypes";

/**
 * The possible values for Segment visibility.
 * Empty string represents "all visibilities".
 */
export const segmentVisibilityValues = ["syndicated", "custom", "shared", ""] as const;

export type SegmentVisibility = (typeof segmentVisibilityValues)[number];

/**
 * The possible values for Segment sort terms.
 */
export const segmentSortTermValues = ["name", "created-desc", "created-asc"] as const;

export type SegmentSortTerm = (typeof segmentSortTermValues)[number];

export type Segment = {
  uuid: string;
  name: string;
  userRefid: number;
  accountRefid: number | null;
  conditionUuid: string;
  deleted: boolean;
  isShared: boolean;
  isFavorite: boolean;
  createdTime?: DateString | null;
  updatedTime?: DateString | null;
  visibility?: SegmentVisibility;
  favoriteUserIds: number[];
  isSyndicated: boolean;
};

export type SegmentResponse = {
  segment: Segment;
  constraints: SampleConditionConstraint[];
  permissions: SegmentPermissionType[];
};

export type SegmentSimple = {
  uuid: string;
  name: string;
  visibility?: SegmentVisibility;
  favoriteUserIds?: number[];
};

export type SaveAsCrosstabState = {
  showModal: boolean;
  crosstabName: string;
};

export type SegmentQueryDataResponse = PagedApiResponse<Segment[]>;

export type ConstraintAnswers = {
  v: number[];
  names: string[];
};

export type ConstraintOption = {
  id: number;
  name: string;
};

export type SegConstraint = {
  uuid: string;
  type: typeof ConstraintTerms.SEG;
};

export const EnabledConstraintTerms = [
  ConstraintTerms.ATT,
  ConstraintTerms.GEO,
  ConstraintTerms.ZIP,
  ConstraintTerms.SEG,
] as const;

export type EnabledConstraintTerm = (typeof EnabledConstraintTerms)[number];

export type AttGeoZipConstraint = {
  type: Exclude<EnabledConstraintTerm, typeof ConstraintTerms.SEG>;
  a?: string;
  v: string[] | number[];
  dateFilter?: ApiDateFilter;
};

export type SampleConditionConstraint = AttGeoZipConstraint | SegConstraint;

type ConstraintBase = {
  id: string;
  name: string;
  type: EnabledConstraintTerm;
};

export type ConstraintSimple = ConstraintBase & {
  /**
   * This is only populated for items with type `ATT` which are also "checkbox items".
   * This mimics the shape used by question search and question detail
   * and makes it possible to display a Question Type Badge with the results.
   *
   * This is obviously very specific to checkbox questions and we'll likely revisit
   * this while working to better handle matrix questions.
   * See: CS-3438
   * @deprecated - use askableParent instead
   */
  checkboxGroup?: { groupId: number; itemText: string; groupText: string }[];
  /**
   * Populated for question Constraints if they have a parent question.
   */
  askableParent?: ConstraintBase;
};

export type ConstraintArg = {
  id?: string;
  uuid?: string;
  name: string;
  type: EnabledConstraintTerm;
  answers?: { names: string[]; v?: string[] | number[] };
  a?: string;
  dateFilter?: ApiDateFilter;
  /** Every answer choice of the question (or geography), not just the selected ones -- the edit modal needs these. */
  options?: ConstraintOption[];
};

export type Constraint = {
  type: ConstraintFilterType;
  args: ConstraintArg[];
  completed?: boolean;
  /**
   * Client-only: the row was added by "Describe an audience" and hasn't been edited since.
   * Never sent to the API -- `formatConstraints` only copies `type` and `args`.
   */
  isSuggested?: boolean;
};

export type SegmentCreateRequest = {
  name: string;
  constraints: {
    type: ConstraintFilterType;
    args: SampleConditionConstraint[];
  }[];
  isSyndicated?: boolean;
};
export type SegmentUpdateRequest = SegmentCreateRequest;

export type SegmentFavorite = {
  id: number;
  segmentUuid: string;
  userRefid: number;
  updatedTime: DateString;
};

/**
 * Models the values used when searching for segments.
 * This includes filter values as well as paging and sorting values.
 */
export type SegmentSearchParams = CSPaginationRequestArgs & {
  query: string;
  visibility: SegmentVisibility;
  favorites: boolean;
  sortTerm: SegmentSortTerm;
};

/**
 * The request parameters for searching segments.
 */
export type GetSegmentsRequest = SegmentSearchParams;

/**
 * The response from the GetSegments (search segments) API call.
 */
export type GetSegmentsResponse = PagedApiResponse<Segment[]>;

export type GetSampleConditionRequest = {
  constraints: {
    type: ConstraintFilterType;
    args: SampleConditionConstraint[];
  }[];
};

export type SegmentCountResponse = {
  count: number;
};

export type ConstraintFilterType = (typeof ConstraintFilters)[keyof typeof ConstraintFilters];

export type BaseConstraintModalProps = {
  title?: string;
  show?: boolean;
  constraint?: Constraint | null;
  animation?: boolean;
};

export type AddConstraintModalProps = BaseConstraintModalProps & {
  onSelectConstraint: (constraint: Constraint) => void;
  onAddConstraintToSource: (
    type: ConstraintFilterType,
    arg: ConstraintArg,
    optionalConstraint?: Constraint | null,
  ) => boolean;
  onCancel: () => void;
  addSource?: string | null;
  segmentId?: string;
  isInModal?: boolean;
};

export type EditConstraintModalProps = BaseConstraintModalProps & {
  onEditConstraint: (constraint: Constraint, index: number) => void;
  onCancel: () => void;
  editingArg: ConstraintArg | null;
  constraintIndex?: number;
};

/**
 * A single profile question to include in an AI-generated segment summary.
 */
export type SegmentAIProfileQuestion = {
  pqid: number;
};

export type SegmentAIProfileRequest = {
  questions: SegmentAIProfileQuestion[];
  /** Optional analyst-supplied guidance on what to emphasize or how to frame the summary. */
  customInstructions?: string;
};

/**
 * Request body for generating draft segment constraints from a free-text prompt.
 */
export type SegmentAIConditionRequest = {
  /** e.g. "white men who make less than 100k a year, and drive a truck" */
  prompt: string;
  /** Include the debugging fields (`audit`, `candidatesConsidered`, etc.) in the response. */
  includeAudit?: boolean;
};

/**
 * A part of the prompt the generated constraints don't cover, for showing the user what's
 * missing (e.g. "Couldn't find a match for: drive trucks").
 *
 * `no_match`: no survey question fit. `unsupported`: something the builder can't generate yet,
 * like a place.
 */
export type SegmentAIConditionUnmatchedPhrase = {
  phrase: string;
  reason: "no_match" | "unsupported";
};

/**
 * A survey question the AI was offered to choose from, with its answer choices.
 */
export type SegmentAICandidateQuestion = {
  id: number;
  text: string;
  answerChoices: { id: number; label: string }[];
};

/**
 * How one AI-proposed selection resolved to real question/option IDs. Debugging only.
 */
export type SegmentAIConditionAuditEntry = {
  source: "demographic" | "question" | "geography";
  /** A demographic dimension name (e.g. "gender"), a candidate question's id, or a geography level (e.g. "state"). */
  identifier: string;
  requestedLabels: string[];
  resolvedLabels: string[];
  unresolvedLabels: string[];
  exclude: boolean;
  /** The either/or set the AI put this selection in; null when it must match on its own. */
  orGroup: string | null;
  status: "resolved" | "partially_resolved" | "dropped";
  reason: string | null;
};

/**
 * One topic the prompt was split into, and which candidate questions its search found.
 * Debugging only.
 */
export type SegmentAITopicSearchAuditEntry = {
  phrasings: string[];
  candidateIdsFound: number[];
  widened: boolean;
  reason: string | null;
};

/**
 * Every search/generation limit in effect for a request. Debugging only.
 */
export type SegmentAIResolutionParameters = {
  minCandidatesPerTopic: number;
  includeRunnerUpCandidates: boolean;
  maxCandidateQuestions: number;
  perPhrasingLimit: number;
  lookbackDays: number | null;
  minResponseCount: number | null;
  maxPhrasingsPerTopic: number;
  extractionModel: string;
  generationModel: string;
};

/**
 * Draft segment constraints generated from a prompt.
 *
 * `constraints` are builder-ready rows -- the same shape a saved segment loads with (names, every
 * answer option, a date filter) -- so they can be appended straight into the builder's state.
 *
 * On generation failure (including an account's generation limit), `error` is set and
 * `constraints` is empty -- render that as a message, not an exception. The debugging fields are
 * only populated when the request set `includeAudit`.
 */
export type SegmentAIConditionResponse = {
  constraints: Constraint[];
  /** e.g. "claude-sonnet-5" */
  model: string | null;
  /** ISO 8601, e.g. "2026-01-01T00:00:00.000Z" */
  generatedAt: string | null;
  /** e.g. "Generation limit reached for this account -- try again later." */
  error: string | null;
  /** Machine-readable reason alongside `error`: "rate_limited", or null for any other failure. */
  errorCode: "rate_limited" | null;
  unmatched: SegmentAIConditionUnmatchedPhrase[];
  audit: SegmentAIConditionAuditEntry[];
  candidatesConsidered: SegmentAICandidateQuestion[];
  knownDimensionsConsidered: string[];
  topicsConsidered: SegmentAITopicSearchAuditEntry[];
  runnerUpCandidates: SegmentAICandidateQuestion[];
  resolutionParameters: SegmentAIResolutionParameters | null;
};
